import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, PauseCircle } from 'lucide-react'
import { useGtmUsageOverviewQuery } from '../gtm-workspace-queries'
import { GtmTopUpDialog } from './gtm-top-up-dialog'
import { Button } from '@/components/ui/button'
import {
  balanceBannerState,
  formatRunOutDate,
} from '@/features/billing/domain/credit-balance'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { cn } from '@/shared/lib/utils'

/**
 * Above the GTM workspace when the balance needs the customer: low (forecast
 * to run out within days) or used up (search and sending are paused). The
 * state is said in words, not only by color.
 */
export function GtmBalanceBanner({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const [topUpOpen, setTopUpOpen] = useState(false)
  const overview = useGtmUsageOverviewQuery(workspaceId)
  const balance = overview.data?.balance
  const state = balanceBannerState(balance)
  if (state === 'none' || !balance) return null

  const depleted = state === 'depleted'
  const date = formatRunOutDate(balance.runOutAt, lang)
  const emails = new Intl.NumberFormat(lang).format(balance.remainingEmails)

  return (
    <>
      <div
        role={depleted ? 'alert' : 'status'}
        className={cn(
          'flex flex-wrap items-center gap-3 border-b px-4 py-3 text-sm sm:px-6',
          depleted
            ? 'border-destructive/30 bg-destructive/10 text-foreground'
            : 'border-warning/30 bg-warning/10 text-foreground',
        )}
      >
        {depleted ? (
          <PauseCircle className="size-4 shrink-0 text-destructive" />
        ) : (
          <AlertTriangle className="size-4 shrink-0 text-warning" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {depleted ? t('ws_balanceDepletedTitle') : t('ws_balanceLowTitle')}
          </p>
          <p className="text-muted-foreground">
            {depleted
              ? t('ws_balanceDepletedText')
              : date
                ? t('ws_balanceLowText', { emails, date })
                : t('ws_balanceLowTextNoDate', { emails })}
          </p>
        </div>
        <Button size="sm" onClick={() => setTopUpOpen(true)}>
          {t('ws_topUp')}
        </Button>
      </div>
      <GtmTopUpDialog
        workspaceId={workspaceId}
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
      />
    </>
  )
}
